import React, { useState, useEffect, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import SubTitle from "../common/SubTitle";
import Cards from "../common/Cards";
import SearchFilter from "../common/SearchFilter";
import Pagination from "../common/Pagination";
import axios from "axios";
import config from "../../config.js";
import { usePermission } from "../../hooks/usePermission";
import DeleteModal from "../common/DeleteModal";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
// Import icons
import Update_Icon from "../../images/table/Update_icon.svg";
import Reset_Icon from "../../images/table/Reset_icon.svg";
import Delete_Icon from "../../images/table/Delete_icon.svg";
import DynamicTable from "../common/DynamicTable";
import { useTranslation } from "react-i18next";
import { CARD_BACKGROUND_COLORS, TEXT_COLORS } from "../../constants/styles";
import { ACTIONS_COLUMN_ID } from "../../constants/dashboardConstants";
import { use } from "react";
import { Box, CircularProgress, IconButton } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
const headers = [
  { key: "name", value: "name" },
  { key: "email", value: "email" },
  { key: "username", value: "username" },
  { key: "roles", value: "roles" },
  { key: ACTIONS_COLUMN_ID, value: "Actions" },
];

export default function UsersMain() {
  const { t } = useTranslation();
  const theme = useTheme();
  const [userData, setUserData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    role: "",
    status: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] =
    useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pass, setPass] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [passwordError, setPasswordError] = useState("");

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const navigate = useNavigate();
  const { hasPermission } = usePermission();

  // Check user permissions for different actions
  const canCreateUser = hasPermission("/api/user", "POST");
  const canUpdateUser = hasPermission("/api/user", "PUT");
  const canDeleteUser = hasPermission("/api/user", "DELETE");
  const canViewUserDetails = hasPermission("/api/user/id", "GET");
  const canResetPassword = hasPermission("/api/user/password", "PATCH");
  const canUpdateRole = hasPermission("/api/user/role", "PATCH");

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Define filter configuration
  const [filterConfig, setFilterConfig] = useState([
    {
      id: "role",
      label: "roles",
      helperText: "Select role",
      options: [],
    },
  ]);

  // Define custom actions with permission checks
  const getCustomActions = () => {
    const actions = [];

    if (canUpdateUser) {
      actions.push({
        id: "update",
        label: "Update",
        icon: Update_Icon,
        path: "/user/AddUser",
      });
    }

    if (canResetPassword) {
      actions.push({
        id: "reset",
        label: "Reset Password",
        icon: Reset_Icon,
        path: "",
      });
    }

    if (canDeleteUser) {
      actions.push({
        id: "delete",
        label: "Delete",
        className: "delete",
        icon: Delete_Icon,
        path: "",
      });
    }

    return actions;
  };

  // Function to fetch all users
  async function getAllUsers() {
    try {
      setIsLoading(true);
      const userState = JSON.parse(localStorage.getItem("userState"));
      const organizationId = userState.user.organizationId;
      const response = await axios.get(
        `${config.baseURL}/user/organization/${organizationId}`,
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      console.log("Error in fetching user data: ", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }

  // Function to delete user
  async function deleteUser(id) {
    if (!canDeleteUser) return;

    try {
      const response = await axios.delete(`${config.baseURL}/user/${id}`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.log("Error in Deleting user data: ", error);
      throw error;
    }
  }

  // Function to update password
  async function updatePassword(id, confirmPass) {
    if (!canResetPassword) return;

    try {
      const response = await axios.patch(
        `${config.baseURL}/user/password`,
        {
          userId: id,
          newPassword: confirmPass,
        },
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating password: ", error);
      throw error;
    }
  }

  // Process user data for display
  function processUserData(userList) {
    return userList.map((user) => ({
      name: user.name || "N/A",
      email: user.email || "N/A",
      userName: user.userName || "N/A",
      id: user.id,
      role: user.roles ? user.roles.name : "N/A",
      status: user.status || "ACTIVE",
      originalData: user,
    }));
  }

  // Fetch users data on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await getAllUsers();
        const users = response.users;
        const processedUsers = processUserData(users);
        setUserData(processedUsers);

        // Extract unique roles for filter
        const uniqueRoles = [];
        const roleSet = new Set();

        users.forEach((user) => {
          if (user.roles && user.roles.name && !roleSet.has(user.roles.name)) {
            roleSet.add(user.roles.name);
            uniqueRoles.push({
              value: user.roles.name,
              label: user.roles.name,
            });
          }
        });

        // Update filter config with dynamic roles
        setFilterConfig((prev) => {
          const updated = [...prev];
          const roleIndex = updated.findIndex((f) => f.id === "role");
          if (roleIndex >= 0) {
            updated[roleIndex].options = uniqueRoles;
          }
          return updated;
        });
      } catch (error) {
        if (error.response && error.response.status === 401) {
          console.log("Unauthorized request");
          navigate("/");
          return;
        }
        console.error("Error fetching user data:", error);
      }
    };

    fetchUsers();
  }, [navigate]);

  // Format data for table display
  const formatDataForTable = (data) => {
    return data.map((user) => {
      const formattedData = {
        name: user.name,
        email: user.email,
        username: user.userName,
        roles: user.role,
      };

      // Add original ID for action handling
      formattedData.id = user.id;
      formattedData.originalData = user.originalData;

      return formattedData;
    });
  };

  // Handle search changes
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1); // Reset to first page on search
  };

  // Handle filter changes
  const handleFilterChange = (filterValues) => {
    setFilters(filterValues);
    setCurrentPage(1); // Reset to first page on filter change
  };

  // Filter data based on search query and filters
  const filteredUserData = userData.filter((user) => {
    // Check if role filter is applied
    if (filters.role && user.role !== filters.role) {
      return false;
    }

    // Check if status filter is applied
    if (filters.status && user.status !== filters.status) {
      return false;
    }

    // If no search query, return true (show all that match filters)
    if (!searchQuery) {
      return true;
    }

    // Otherwise, search in all relevant fields
    const query = searchQuery.toLowerCase();
    return (
      (user.name && user.name.toLowerCase().includes(query)) ||
      (user.email && user.email.toLowerCase().includes(query)) ||
      (user.userName && user.userName.toLowerCase().includes(query)) ||
      (user.role && user.role.toLowerCase().includes(query))
    );
  });

  // Handle rows per page change
  const handleRowsPerPageChange = (newRowsPerPage) => {
    setItemsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  // Add user handler
  const handleAddUser = () => {
    if (canCreateUser) {
      navigate("/user/addUser");
    } else {
      // You could show a permission error message here
      console.log("You don't have permission to create users");
    }
  };

  // Add role handler
  const handleAddRole = () => {
    if (canUpdateRole) {
      navigate("/user/role");
    } else {
      // You could show a permission error message here
      console.log("You don't have permission to manage roles");
    }
  };

  // Delete modal handlers
  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setSelectedUser(null);
  };

  const handleDeleteConfirm = async () => {
    if (selectedUser && canDeleteUser) {
      try {
        await deleteUser(selectedUser.id);

        // Remove the deleted user from the state
        setUserData((prevData) =>
          prevData.filter((item) => item.id !== selectedUser.id)
        );
        window.location.reload();
      } catch (error) {
        console.log("Error deleting user: ", error);
      }
    }
    setIsDeleteModalOpen(false);
    setSelectedUser(null);
  };

  // Reset password modal handlers
  const handleResetPasswordCancel = () => {
    setIsResetPasswordModalOpen(false);
    setPass({
      newPassword: "",
      confirmPassword: "",
    });
  };

  const handleResetPasswordConfirm = async () => {
    const { newPassword, confirmPassword } = pass;
    if (
      newPassword !== "" &&
      confirmPassword !== "" &&
      newPassword === confirmPassword &&
      canResetPassword
    ) {
      try {
        await updatePassword(selectedUser.id, newPassword);
        setIsResetPasswordModalOpen(false);
        setPass({
          newPassword: "",
          confirmPassword: "",
        });
      } catch (error) {
        console.error("Error updating password:", error);
      }
    } else {
      setPasswordError("Passwords do not match or are empty");
    }
  };

  // Handle password change in form
  const handlePassChange = (e) => {
    const { name, value } = e.target;
    setPass((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setPasswordError("");
  };

  // Handle action click - with permission checks
  const handleActionClick = (actionId, rowIndex, actionObj) => {
    // Get the user data for this row
    const user = filteredUserData[rowIndex];

    switch (actionId) {
      case "update":
        if (canUpdateUser) {
          navigate(actionObj.path, { state: { id: user.id } });
        }
        break;
      case "reset":
        if (canResetPassword) {
          setSelectedUser(user);
          setIsResetPasswordModalOpen(true);
        }
        break;
      case "delete":
        if (canDeleteUser) {
          setSelectedUser(user);
          setIsDeleteModalOpen(true);
        }
        break;
      default:
        break;
    }
  };

  // Calculate pagination
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredUserData.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredUserData.length / itemsPerPage);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  console.log(userData);
  // Get counts for cards
  // const activeUsersCount = userData.filter(user => user.status === "ACTIVE").length || 0;
  // const inactiveUsersCount = userData.filter(user => user.status === "INACTIVE").length || 0;

  const activeUsersCount =
    userData.filter((user) => user.role && user.role !== "N/A").length || 0;
  const inactiveUsersCount =
    userData.filter((user) => !user.role || user.role === "N/A").length || 0;

  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;
  return (
    <div style={{ minHeight: "100vh" }}>
      <SubTitle
        title="Users"
        path={`${orgName || "organization"} > users`}
        add={canCreateUser ? "createUser" : null}
        addPath="/user/add"
        onAddClick={handleAddUser}
        track={canUpdateRole ? "manageRoles" : null}
        trackPath="/user/role"
        onTrackClick={handleAddRole}
      />

      <Cards
        cardData={[
          {
            title: "totalUsers",
            value: userData?.length || 0,
            backgroundColor: CARD_BACKGROUND_COLORS.DARK_BLUE,
            titleColor: TEXT_COLORS.WHITE,
            valueColor: TEXT_COLORS.WHITE,
          },
          {
            title: "activeUsers",
            value: activeUsersCount,
            backgroundColor: CARD_BACKGROUND_COLORS.GREEN,
            titleColor: TEXT_COLORS.WHITE,
            valueColor: TEXT_COLORS.WHITE,
          },
          {
            title: "inactiveUsers",
            value: inactiveUsersCount,
            backgroundColor: CARD_BACKGROUND_COLORS.YELLOW,
            titleColor: TEXT_COLORS.BLACK,
            valueColor: TEXT_COLORS.BLACK,
          },
        ]}
      />

      <div className="table_container">
        <SearchFilter
          filters={filterConfig}
          searchPlaceholder="search"
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
        />

        
        <Suspense fallback={<CircularProgress />}>
          <DynamicTable
            headers={headers}
            data={formatDataForTable(filteredUserData || [])}
            onActionClick={handleActionClick}
            actions={getCustomActions()}
            loading={isLoading}
          />
        </Suspense>
        

        {/* <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                /> */}
      </div>

      {/* Delete Modal - Using the common DeleteModal component */}
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete User!"
        message={
          <>
            {t("Are you sure you want to delete")}{" "}
            <span style={{ fontWeight: 600, color: "#d32f2f" }}>
              {selectedUser?.name || t("this user")}
            </span>
            ? {t("This action cannot be undone.")}
          </>
        }
      />

      {/* Reset Password Modal */}
      <Dialog
        open={isResetPasswordModalOpen}
        onClose={handleResetPasswordCancel}
        PaperProps={{
          style: {
            backgroundColor: "#FFFFFF",
            width: isMobile ? "90%" : isTablet ? "80%" : "594px",
            height: isMobile ? "auto" : "424px",
            borderRadius: isMobile ? "16px" : "20px",
            maxWidth: "600px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            padding: isMobile
              ? "24px 16px"
              : isTablet
              ? "32px 40px"
              : "40px 80px",
            border: "1px solid #A5DCF3",
            boxShadow: "-2px 2px 5px 0px #00000040",
            gap: isMobile ? "24px" : "32px",
          },
        }}
      >
        <DialogTitle
          style={{
            fontFamily: "Roboto",
            fontWeight: "500",
            fontSize: isMobile ? "24px" : isTablet ? "26px" : "28px",
            lineHeight: "100%",
            letterSpacing: "0%",
            textAlign: "center",
            color: "#183059",
            padding: "0",
            margin: "0",
          }}
        >
          Reset Password
        </DialogTitle>

        <DialogContent
          sx={{
            padding: "0",
            margin: "0",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          <div>
            <h3
              style={{
                fontFamily: "Roboto",
                fontWeight: "400",
                fontSize: isMobile ? "18px" : "20px",
                lineHeight: "100%",
                letterSpacing: "0%",
                color: "#1F242E",
                margin: "0 0 12px 0",
              }}
            >
              New Password <span style={{ color: "#EE3B2B" }}>*</span>
            </h3>
            <TextField
              fullWidth
              placeholder="Enter new password"
              type={showNewPassword ? "text" : "password"}
              autoComplete="new-password"
              name="newPassword"
              value={pass.newPassword}
              onChange={handlePassChange}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
              sx={{
                height: "55px",
                margin: "0",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "16px",
                  border: `2px solid ${passwordError ? "red" : "#183059"}`,
                  padding: "16px 24px",
                  fontSize: "16px",
                  backgroundColor: "#FFFFFF",
                  height: "55px",
                  "& fieldset": {
                    border: "none",
                  },
                  "&:hover fieldset": {
                    border: "none",
                  },
                  "&.Mui-focused fieldset": {
                    border: "none",
                  },
                },
                "& .MuiOutlinedInput-input": {
                  padding: "0",
                  height: "auto",
                },
              }}
            />
          </div>

          <div>
            <h3
              style={{
                fontFamily: "Roboto",
                fontWeight: "400",
                fontSize: isMobile ? "18px" : "20px",
                lineHeight: "100%",
                letterSpacing: "0%",
                color: "#1F242E",
                margin: "0 0 12px 0",
              }}
            >
              Confirm New Password <span style={{ color: "#EE3B2B" }}>*</span>
            </h3>
            <TextField
              fullWidth
              placeholder="Confirm your new password"
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              name="confirmPassword"
              value={pass.confirmPassword}
              onChange={handlePassChange}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
              sx={{
                height: "55px",
                margin: "0",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "16px",
                  border: `2px solid ${passwordError ? "red" : "#183059"}`,
                  padding: "16px 24px",
                  fontSize: "16px",
                  backgroundColor: "#FFFFFF",
                  height: "55px",
                  "& fieldset": {
                    border: "none",
                  },
                  "&:hover fieldset": {
                    border: "none",
                  },
                  "&.Mui-focused fieldset": {
                    border: "none",
                  },
                },
                "& .MuiOutlinedInput-input": {
                  padding: "0",
                  height: "auto",
                },
              }}
            />
          </div>

          {passwordError && (
            <p
              style={{
                color: "red",
                margin: "8px 0 0 0",
                fontFamily: "Roboto",
                fontSize: "14px",
              }}
            >
              {passwordError}
            </p>
          )}
        </DialogContent>

        <DialogActions
          style={{
            padding: "0",
            gap: isMobile ? "20px" : "64px",
            justifyContent: "center",
            flexDirection: isMobile ? "column" : "row",
            margin: "0",
          }}
        >
          <button
            onClick={handleResetPasswordCancel}
            className="customButton_add"
            style={{
              width: isMobile ? "100%" : "122px",
              height: "55px",
              borderRadius: "8px",
              border: "1px solid #414958",
              padding: "16px 24px",
              backgroundColor: "#F2F2F3",
              fontFamily: "Roboto",
              fontSize: isMobile ? "18px" : "24px",
              fontWeight: "600",
              textTransform: "none",
              cursor: "pointer",
              color: "#000000",
            }}
          >
            Cancel
          </button>

          <button
            className="customButton_add"
            onClick={handleResetPasswordConfirm}
            style={{
              width: isMobile ? "100%" : "125px",
              height: "55px",
              borderRadius: "8px",
              border: "1px solid #414958",
              padding: "16px 24px",
              backgroundColor: "#F9E719",
              fontFamily: "Roboto",
              fontSize: isMobile ? "18px" : "24px",
              fontWeight: "600",
              textTransform: "none",
              cursor: "pointer",
              color: "#1F242E",
            }}
          >
            Confirm
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
